import { useDroppable } from "@dnd-kit/core";
import { Paper, Stack } from "@mui/material";
import { GridColDef, GridRowProps, GridRowSelectionModel, useGridApiRef } from "@mui/x-data-grid";
import { GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import React, { MutableRefObject, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbList } from "react-icons/tb";

import CommonGridTable from "@/components/CommonGridTable";
import { CompanionsColumn } from "@/components/table/CompanionsColumn";
import FlightNumberColumn from "@/components/table/FlightNumberColumn";
import FlightTimeColumn from "@/components/table/FlightTimeColumn";
import { PassengerColumn } from "@/components/table/PassengerColumn";
import PassengerStatusColumn from "@/components/table/PassengerStatusColumn";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import PlanOrderColumn from "@/pages/Plannn/components/table/PlanOrderColumn";
import {
    TRACKING_TASK_DROP_PREFIX,
    TrackingOverTarget,
    useTrackingDndDrag,
    useTrackingDndOverTaskId,
    useTrackingDndOverTaskIsValid,
} from "@/stores/use-tracking-dnd";
import { IPlanPassenger, Task } from "@/types/tasks.types";

import DraggableTrackingPassengerRow from "./DraggableTrackingPassengerRow";
import TrackingDetailActionsColumn from "./TrackingDetailActionsColumn";

function TrackingDetailTable({ row }: { row: Task<IPlanPassenger<"api">> }) {
    const { t } = useTranslation();
    const api = useGridApiRef() as MutableRefObject<GridApiPremium>;
    const { size } = useWindowDimensions();
    const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>([]);

    const targetData: TrackingOverTarget = useMemo(
        () => ({ taskId: row.id, isArrival: row.isArrival }),
        [row.id, row.isArrival]
    );
    const { setNodeRef } = useDroppable({
        id: `${TRACKING_TASK_DROP_PREFIX}${row.id}:inner`,
        data: targetData,
    });

    const overTaskId = useTrackingDndOverTaskId();
    const isOver = overTaskId === row.id;
    const isValid = useTrackingDndOverTaskIsValid();
    const highlightInvalid = isOver && isValid === false;
    const highlightValid = isOver && isValid !== false;
    const drag = useTrackingDndDrag();

    const rows = useMemo(() => {
        const base = (row.passengers || []).map((p) => ({ ...p, _taskId: row.id }));

        const hasPlaceholder = highlightValid && drag !== null && !base.some((p) => p.id === drag.id);
        if (!hasPlaceholder) return base;

        const placeholderKey = `__dnd_ph__${row.id}`;
        return [
            ...base,
            {
                ...(base[0] ?? {}),
                id: placeholderKey,
                name: drag.name,
                _taskId: row.id,
                _isPlaceholder: true,
            },
        ];
    }, [row, highlightValid, drag]);

    const columns = useMemo<GridColDef[]>(
        () => [
            {
                ...GRID_CHECKBOX_SELECTION_COL_DEF,
                headerClassName: "checkbox-header",
            },
            PlanOrderColumn(),
            PassengerColumn({ t, raw: false, size }),
            CompanionsColumn({ t, editable: false }),
            FlightTimeColumn({ t }),
            FlightNumberColumn({ t }),
            PassengerStatusColumn({ t, editable: false }),
            TrackingDetailActionsColumn({ allRows: rows, selectedRowIds }),
        ],
        [rows, size, selectedRowIds]
    );

    return (
        <Stack
            ref={setNodeRef}
            sx={{
                py: 1,
                boxSizing: "border-box",
                backgroundColor: highlightInvalid
                    ? "rgba(239,68,68,0.07)"
                    : highlightValid
                      ? "rgba(20,184,166,0.07)"
                      : undefined,
                boxShadow: highlightInvalid
                    ? "inset 0 0 0 2px rgba(239,68,68,0.7)"
                    : highlightValid
                      ? "inset 0 0 0 2px rgba(20,184,166,0.7)"
                      : "none",
                transition: "background-color 150ms ease, box-shadow 150ms ease",
            }}
            direction="column"
        >
            <Paper sx={{ flex: 1, mx: "auto", p: 1 }} className=" w-[98%] lg:w-[95%]">
                <Stack direction="column" spacing={1} sx={{ height: 1 }}>
                    <div className="flex justify-between">
                        <div className={`font-semibold  flex items-center gap-2 text-sm`}>
                            <TbList /> Yolcu Listesi
                        </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                        <CommonGridTable
                            checkboxSelection
                            hideFooter
                            columnHeaderHeight={35}
                            api={api}
                            rows={rows}
                            columns={columns}
                            loading={false}
                            allRows={rows}
                            disableColumnFilter
                            disableColumnPinning
                            disableColumnReorder
                            disableColumnMenu
                            isRowSelectable={(params) => !params.row._isPlaceholder}
                            onRowSelectionModelChange={setSelectedRowIds}
                            CustomGridRow={DraggableTrackingPassengerRow as React.ComponentType<GridRowProps>}
                        />
                    </div>
                </Stack>
            </Paper>
        </Stack>
    );
}

export default TrackingDetailTable;
